/**
 * TanStack Query hooks for the Notifications admin feature.
 *
 * Most endpoints aren't published in `api.d.ts`, so we keep the typed
 * client (auth + retry middleware) but cast the path / body to `never`
 * to bypass the schema mismatch. Every cast carries a TODO; once the
 * backend ships the new paths the casts can be dropped in one pass.
 *
 * Wire shapes live in `./types`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type {
  CreateTemplateRequest,
  HistoryDetailResponse,
  HistoryPage,
  HistoryQueryFilters,
  HistoryRow,
  NotificationScheduleRequest,
  NotificationSendRequest,
  NotificationSendResponse,
  NotificationTemplateResponse,
  ScheduledNotification,
  ScheduledQueryFilters,
  UpdateTemplateRequest,
} from './types';

export type * from './types';

/* ------------------------------------------------------------------ */
/* Response normalisation                                              */
/* ------------------------------------------------------------------ */

/**
 * Coerces an admin endpoint payload into a flat array of `T`.
 *
 * The backend may return either a Spring `Page<T>` envelope
 * (`{ content: T[], page, size, totalElements, totalPages }`) or a flat
 * `T[]`. Older code assumed the latter and crashed in production with
 * `TypeError: t.map is not a function` on the Templates and Scheduled
 * tabs (see DELTA-AUDIT-after-pr18.md §2.2).
 *
 * Returns an empty array for `null`, `undefined`, or any other shape we
 * don't recognise, so callers never have to defend against `.map` on a
 * non-array.
 */
export function normalizeListResponse<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (
    typeof value === 'object' &&
    value !== null &&
    'content' in value &&
    Array.isArray((value as { content?: unknown }).content)
  ) {
    return (value as { content: T[] }).content;
  }
  return [];
}

export const notificationsKeys = {
  all: ['notifications'] as const,
  templates: () => [...notificationsKeys.all, 'templates'] as const,
  scheduled: (filters: ScheduledQueryFilters) =>
    [...notificationsKeys.all, 'scheduled', filters] as const,
  history: (filters: HistoryQueryFilters) =>
    [...notificationsKeys.all, 'history', filters] as const,
  historyDetail: (id: string) =>
    [...notificationsKeys.all, 'history', 'detail', id] as const,
};

/* ------------------------------------------------------------------ */
/* Templates CRUD                                                      */
/* ------------------------------------------------------------------ */

/**
 * Lists all notification templates from `/api/v1/admin/notification-templates`.
 * TODO: align once the backend publishes that path in OpenAPI.
 */
export function useTemplates(): UseQueryResult<NotificationTemplateResponse[], Error> {
  return useQuery<NotificationTemplateResponse[], Error>({
    queryKey: notificationsKeys.templates(),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/api/v1/admin/notification-templates' as never,
      );
      if (error) throw error as Error;
      return normalizeListResponse<NotificationTemplateResponse>(data);
    },
  });
}

export function useCreateTemplate(): UseMutationResult<
  NotificationTemplateResponse,
  Error,
  CreateTemplateRequest
> {
  const queryClient = useQueryClient();
  return useMutation<NotificationTemplateResponse, Error, CreateTemplateRequest>({
    mutationFn: async (body) => {
      const { data, error } = await api.POST(
        '/api/v1/admin/notification-templates' as never,
        { body } as never,
      );
      if (error) throw error as Error;
      return data as NotificationTemplateResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.templates() });
    },
  });
}

export function useUpdateTemplate(): UseMutationResult<
  NotificationTemplateResponse,
  Error,
  { id: string; body: UpdateTemplateRequest }
> {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationTemplateResponse,
    Error,
    { id: string; body: UpdateTemplateRequest }
  >({
    mutationFn: async ({ id, body }) => {
      const { data, error } = await api.PUT(
        '/api/v1/admin/notification-templates/{id}' as never,
        { params: { path: { id } }, body } as never,
      );
      if (error) throw error as Error;
      return data as NotificationTemplateResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.templates() });
    },
  });
}

export function useDeleteTemplate(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await api.DELETE(
        '/api/v1/admin/notification-templates/{id}' as never,
        { params: { path: { id } } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.templates() });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Send / Schedule                                                     */
/* ------------------------------------------------------------------ */

/**
 * Variables for `useSendNotification`. The body matches the v2
 * `SendNotificationRequest`; `confirmedLargeBroadcast` travels as the
 * `?confirmedLargeBroadcast=true` query param documented in
 * `AdminNotificationController.send`.
 */
export interface SendNotificationVars {
  body: NotificationSendRequest;
  confirmedLargeBroadcast?: boolean;
}

/**
 * Sends an immediate push using the v2 contract:
 * `{ templateCode, targetType, targetUserIds?, targetRole?, variables? }`.
 *
 * The OpenAPI types still mismatch (the path isn't in `api.d.ts` yet), so
 * we keep the `as never` casts but the typed surface here is the v2 shape.
 * See DELTA-AUDIT-after-pr18.md §2.1 for context.
 */
export function useSendNotification(): UseMutationResult<
  NotificationSendResponse,
  Error,
  SendNotificationVars
> {
  const queryClient = useQueryClient();
  return useMutation<NotificationSendResponse, Error, SendNotificationVars>({
    mutationFn: async ({ body, confirmedLargeBroadcast }) => {
      const query: Record<string, boolean> = {};
      if (confirmedLargeBroadcast) query.confirmedLargeBroadcast = true;
      const { data, error } = await api.POST(
        '/api/v1/admin/notifications/send' as never,
        { params: { query }, body } as never,
      );
      if (error) throw error as Error;
      return data as NotificationSendResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}

/** Variables for `useScheduleNotification` — same shape as send + `sendAt`. */
export interface ScheduleNotificationVars {
  body: NotificationScheduleRequest;
  confirmedLargeBroadcast?: boolean;
}

/**
 * Creates a future-dated push using the v2 schedule contract.
 * Backend field is `sendAt` (ISO `OffsetDateTime`), not `scheduledAt`.
 */
export function useScheduleNotification(): UseMutationResult<
  NotificationSendResponse,
  Error,
  ScheduleNotificationVars
> {
  const queryClient = useQueryClient();
  return useMutation<NotificationSendResponse, Error, ScheduleNotificationVars>({
    mutationFn: async ({ body, confirmedLargeBroadcast }) => {
      const query: Record<string, boolean> = {};
      if (confirmedLargeBroadcast) query.confirmedLargeBroadcast = true;
      const { data, error } = await api.POST(
        '/api/v1/admin/notifications/schedule' as never,
        { params: { query }, body } as never,
      );
      if (error) throw error as Error;
      return data as NotificationSendResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Scheduled list / cancel                                             */
/* ------------------------------------------------------------------ */

/**
 * Lists scheduled notifications. `'ALL'` is local-only: the backend
 * treats a missing `status` as "no filter". `as never` cast on the path.
 */
export function useScheduledList(
  filters: ScheduledQueryFilters,
): UseQueryResult<ScheduledNotification[], Error> {
  return useQuery<ScheduledNotification[], Error>({
    queryKey: notificationsKeys.scheduled(filters),
    queryFn: async () => {
      const query: Record<string, string | number> = {
        size: filters.size ?? 100,
      };
      if (filters.status && filters.status !== 'ALL') query.status = filters.status;
      if (filters.date) query.date = filters.date;
      const { data, error } = await api.GET(
        '/api/v1/admin/notifications/scheduled' as never,
        { params: { query } } as never,
      );
      if (error) throw error as Error;
      return normalizeListResponse<ScheduledNotification>(data);
    },
  });
}

export function useCancelScheduled(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await api.DELETE(
        '/api/v1/admin/notifications/scheduled/{id}' as never,
        { params: { path: { id } } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/* History                                                             */
/* ------------------------------------------------------------------ */

/**
 * Paginated history. The response is normalised to `HistoryPage` whether
 * the backend returns Spring's `Page<>` envelope or a flat array.
 */
export function useHistoryList(
  filters: HistoryQueryFilters,
): UseQueryResult<HistoryPage, Error> {
  return useQuery<HistoryPage, Error>({
    queryKey: notificationsKeys.history(filters),
    queryFn: async () => {
      const query: Record<string, string | number> = {
        page: filters.page,
        size: filters.size,
      };
      if (filters.status && filters.status !== 'ALL') query.status = filters.status;
      const { data, error } = await api.GET(
        '/api/v1/admin/notifications/history' as never,
        { params: { query } } as never,
      );
      if (error) throw error as Error;
      return normaliseHistoryPage(data, filters);
    },
  });
}

export function useHistoryDetail(
  id: string | undefined,
): UseQueryResult<HistoryDetailResponse, Error> {
  return useQuery<HistoryDetailResponse, Error>({
    queryKey: notificationsKeys.historyDetail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/api/v1/admin/notifications/history/{id}' as never,
        { params: { path: { id: id ?? '' } } } as never,
      );
      if (error) throw error as Error;
      return data as HistoryDetailResponse;
    },
  });
}

/**
 * Coerces the history endpoint payload into `HistoryPage`. Reuses
 * `normalizeListResponse` for the array extraction and only layers on the
 * page metadata. Optional fields are conditionally assigned to satisfy
 * `exactOptionalPropertyTypes: true`.
 */
function normaliseHistoryPage(value: unknown, filters: HistoryQueryFilters): HistoryPage {
  const content = normalizeListResponse<HistoryRow>(value);

  if (Array.isArray(value)) {
    return {
      content,
      totalElements: content.length,
      totalPages: 1,
      page: 0,
      size: filters.size,
    };
  }

  if (typeof value === 'object' && value !== null && 'content' in value) {
    const v = value as Partial<HistoryPage>;
    const result: HistoryPage = { content };
    if (typeof v.totalElements === 'number') result.totalElements = v.totalElements;
    if (typeof v.totalPages === 'number') result.totalPages = v.totalPages;
    if (typeof v.page === 'number') result.page = v.page;
    if (typeof v.size === 'number') result.size = v.size;
    return result;
  }

  return { content, totalElements: 0, totalPages: 0, page: filters.page, size: filters.size };
}
